import { useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { matchCandidates } from '../../../ctapi/candidates';

export const useCandidateSearch = (firebaseUser) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [candidates, setCandidates] = useState([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'final_score', direction: 'desc' });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchHistory, setSearchHistory] = useState(() => JSON.parse(localStorage.getItem('passiveSearchHistory') || '[]'));

    const handleSortRequest = useCallback((property) => {
        const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
        setSortConfig({ key: property, direction: isAsc ? 'desc' : 'asc' });
        setPage(0);
    }, [sortConfig]);

    const sortedCandidates = useMemo(() => {
        let sortableItems = [...candidates];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                const valA = a[sortConfig.key] ?? (sortConfig.direction === 'asc' ? Infinity : -Infinity);
                const valB = b[sortConfig.key] ?? (sortConfig.direction === 'asc' ? Infinity : -Infinity);
                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [candidates, sortConfig]);

    const addToHistory = (query) => {
        if (!query || query.trim().length < 10) return;
        const newHistory = [query, ...searchHistory.filter(item => item !== query)].slice(0, 5);
        setSearchHistory(newHistory);
        localStorage.setItem('passiveSearchHistory', JSON.stringify(newHistory));
    };

    const handleSubmitSearch = useCallback(async (searchQuery) => {
        if (!searchQuery || searchQuery.trim() === '') {
            setError("Please enter a job description to start your search.");
            return;
        }
        if (!firebaseUser) {
            setError("User not authenticated. Please sign in to search for candidates.");
            return;
        }

        setIsProcessing(true);
        setError(null);
        setCandidates([]);
        setPage(0);
        setSortConfig({ key: 'final_score', direction: 'desc' });
        
        // Update URL and history
        navigate(`/passive-search?q=${encodeURIComponent(searchQuery)}`);
        addToHistory(searchQuery);

        try {
            const resultData = await matchCandidates(searchQuery);
            const rawCandidates = resultData.candidates || [];
            setCandidates(rawCandidates);

            if (rawCandidates.length === 0) {
                setError("No candidates found matching your description. Try refining your search terms.");
            }
        } catch (err) {
            console.error("Error submitting search:", err);
            setError(err.message || "An unexpected error occurred.");
            setCandidates([]);
        } finally {
            setIsProcessing(false);
        }
    }, [firebaseUser, navigate]);

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    const paginatedCandidates = useMemo(() => {
        return sortedCandidates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedCandidates, page, rowsPerPage]);

    // Effect to handle initial search from URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const queryFromUrl = params.get('q');
        if (queryFromUrl && candidates.length === 0 && !isProcessing) {
             handleSubmitSearch(decodeURIComponent(queryFromUrl));
        }
    }, [location.search]);


    return {
        handleSubmitSearch,
        sortedCandidates,
        paginatedCandidates,
        isProcessing,
        error,
        sortConfig,
        handleSortRequest,
        page,
        rowsPerPage,
        handleChangePage,
        handleChangeRowsPerPage,
        searchHistory,
        rowsPerPageOptions: [10, 25, 50, 100],
    };
};
